// frontend/src/features/livestock/LineUpModal.tsx
// Line up a photo against its subject's reference photo
// (docs/plans/2026-10-29-tank-shot-framing-design.md, 3 and 7.2). PhotoLineUpModal
// takes the save and make-reference actions as callbacks, so it serves any
// subject (livestock compare design 8.1); LineUpModal is the tank shot one.
//
// Full screen, over the lightbox: a frame of the reference's shape, the shot
// inside it, and the reference over it as a ghost (see-through) or in
// Difference (edges that line up go dark). One finger or the mouse drags the
// shot; pinch, ctrl+wheel or a trackpad pinch zooms about the fingers or the
// pointer; rotation is a slider. Keyboard on the frame: arrows pan (shift for
// more), + and - zoom, [ and ] rotate. Save stores the framing on the shot;
// the original photo is never changed.
//
// With no reference yet (for a tank, no chosen cover), it asks to make this
// photo the reference instead: photos are never lined up against a moving target.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Modal,
  SegmentedControl,
  SimpleGrid,
  Slider,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconArrowBackUp, IconEyeOff, IconX } from '@tabler/icons-react';
import type { PhotoFraming } from '@/types/photos';
import { useSetTankCover, useSetTankPhotoFraming } from '@/hooks/useTankPhotos';
import { ratioOf } from './CompareImage';
import {
  MAX_ROTATION,
  forSaving,
  framedImageStyle,
  framedRatio,
  initialFit,
  panBy,
  rotateTo,
  withAspect,
  zoomAbout,
  zoomOf,
} from './framing';
import { lineUpMode, type LineUpReference } from './lineUpReference';
import { wheelPixels } from './wheelDelta';
import { LIGHTBOX_Z_INDEX } from './PhotoLightbox';
import { formatPhotoDay } from './photoInboxFormat';
import { TANK_WORDS, capitalise, type SubjectWords } from './photoSubject';
import classes from './LineUp.module.css';

/** The photo being lined up. */
export interface LineUpShot {
  id: number;
  entry_date: string;
  /** Signed URL, 1920 px */
  photo_url: string | null;
  /** Signed URL, 800 px: shown until the full variant has loaded */
  photo_display_url: string | null;
  framing?: PhotoFraming | null;
}

export interface PhotoLineUpModalProps {
  /** The photo to line up; null closes it. */
  shot: LineUpShot | null;
  reference: LineUpReference | null;
  words: SubjectWords;
  /** Save the framing (rounded by forSaving, with ref_photo_id). Rejects to keep the editor open. */
  onSave: (id: number, framing: PhotoFraming) => Promise<void>;
  /** "Use this photo": make it the reference. Rejects to keep the question open. */
  onMakeReference: (id: number) => Promise<void>;
  onClose: () => void;
}

export interface LineUpModalProps {
  tankId: string;
  /** The shot to line up; null closes it. */
  shot: LineUpShot | null;
  reference: LineUpReference | null;
  onClose: () => void;
}

/** Over the lightbox and the compare page's full screen. */
export const LINE_UP_Z_INDEX = LIGHTBOX_Z_INDEX + 50;
export const ZOOM_MIN = 0.25;
export const ZOOM_MAX = 4;
const KEY_PAN = 0.01;
const KEY_PAN_FAST = 0.1;
const KEY_ZOOM = 1.1;
const KEY_TURN = 0.1;
const KEY_TURN_FAST = 1;
const DEFAULT_OPACITY = 40;
/** A wheel notch (in pixels) zooms by at most this much. */
const WHEEL_ZOOM_MAX = 50;

type GhostStyle = 'ghost' | 'difference';

const GHOST_STYLES: { value: GhostStyle; label: string }[] = [
  { value: 'ghost', label: 'Ghost' },
  { value: 'difference', label: 'Difference' },
];

interface Pointer {
  x: number;
  y: number;
}

/**
 * True while the component is mounted: an awaited action that closes it (and so
 * unmounts it) doesn't then set state on it.
 */
function useMounted(): { current: boolean } {
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return mounted;
}

/** Tank shots: the tank's reference is its chosen cover, saved through the tank shot hooks. */
export function LineUpModal({ tankId, shot, reference, onClose }: LineUpModalProps): JSX.Element {
  const { mutateAsync: saveFraming } = useSetTankPhotoFraming(tankId);
  const { mutateAsync: setCover } = useSetTankCover(tankId);
  const onSave = useCallback(
    async (id: number, framing: PhotoFraming): Promise<void> => {
      await saveFraming({ id, framing });
    },
    [saveFraming]
  );
  const onMakeReference = useCallback(
    async (id: number): Promise<void> => {
      await setCover(id);
    },
    [setCover]
  );
  return (
    <PhotoLineUpModal
      shot={shot}
      reference={reference}
      words={TANK_WORDS}
      onSave={onSave}
      onMakeReference={onMakeReference}
      onClose={onClose}
    />
  );
}

/** A 44 px touch target round each slider and its thumb on a phone (LineUp.module.css). */
const SLIDER_CLASSES = { root: classes.slider, thumb: classes.sliderThumb };

const LINE_UP_MODAL_STYLES = {
  content: { backgroundColor: 'var(--rdh-lightbox-bg)' },
  header: { display: 'none' },
  body: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    color: 'var(--mantine-color-gray-0)',
  },
} as const;

export function PhotoLineUpModal({
  shot,
  reference,
  words,
  onSave,
  onMakeReference,
  onClose,
}: PhotoLineUpModalProps): JSX.Element {
  const mode = shot ? lineUpMode(shot.id, reference) : null;
  return (
    <>
      <ChooseReference
        shotId={mode === 'choose' && shot ? shot.id : null}
        words={words}
        onMakeReference={onMakeReference}
        onClose={onClose}
      />
      <Modal
        opened={mode === 'edit'}
        onClose={onClose}
        fullScreen
        padding={0}
        withCloseButton={false}
        zIndex={LINE_UP_Z_INDEX}
        title="Line up"
        overlayProps={{ backgroundOpacity: 0.9, color: 'var(--mantine-color-black)', blur: 0 }}
        // Mantine's own stylesheet loads after the app's CSS modules (main.tsx), so the
        // modal's parts are styled through its styles API, as PhotoLightbox and
        // CompareFullScreen do: the lightbox's dark ground in light and dark mode,
        // no Mantine header (the editor has its own title and close), and the body
        // filling the screen so Save and Cancel stay in view.
        styles={LINE_UP_MODAL_STYLES}
      >
        {mode === 'edit' && shot && reference && (
          // Keyed on the shot, so another shot starts afresh
          <LineUpEditor
            key={shot.id}
            shot={shot}
            reference={reference}
            words={words}
            onSave={onSave}
            onClose={onClose}
          />
        )}
      </Modal>
    </>
  );
}

/** No reference yet: make this photo the reference (for a tank, its cover). */
function ChooseReference({
  shotId,
  words,
  onMakeReference,
  onClose,
}: {
  shotId: number | null;
  words: SubjectWords;
  onMakeReference: (id: number) => Promise<void>;
  onClose: () => void;
}): JSX.Element {
  const mounted = useMounted();
  const [pending, setPending] = useState(false);
  const chooseThisShot = async (): Promise<void> => {
    if (shotId === null) return;
    setPending(true);
    try {
      await onMakeReference(shotId);
      onClose();
    } catch {
      // The caller says what went wrong; the question stays open.
    } finally {
      if (mounted.current) setPending(false);
    }
  };
  return (
    <Modal
      opened={shotId !== null}
      onClose={onClose}
      title={words.chooseReferenceTitle}
      size="sm"
      centered
      zIndex={LINE_UP_Z_INDEX}
    >
      <Stack gap="md">
        <Text size="sm">{words.chooseReferenceBody}</Text>
        <Text size="sm" c="dimmed">
          {words.chooseReferenceDetail}
        </Text>
        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" color="gray" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={() => void chooseThisShot()} loading={pending}>
            {words.chooseReferenceButton}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function LineUpEditor({
  shot,
  reference,
  words,
  onSave: save,
  onClose,
}: {
  shot: LineUpShot;
  reference: LineUpReference;
  words: SubjectWords;
  onSave: (id: number, framing: PhotoFraming) => Promise<void>;
  onClose: () => void;
}): JSX.Element {
  const mounted = useMounted();
  const [saving, setSaving] = useState(false);
  const frameRef = useRef<HTMLDivElement>(null);
  const [shotRatio, setShotRatio] = useState<number | null>(null);
  const [refRatio, setRefRatio] = useState<number | null>(null);
  const [framing, setFraming] = useState<PhotoFraming | null>(null);
  const [fullLoaded, setFullLoaded] = useState(false);
  const [ghostStyle, setGhostStyle] = useState<GhostStyle>('ghost');
  const [opacity, setOpacity] = useState(DEFAULT_OPACITY);
  const [ghostHidden, setGhostHidden] = useState(false);
  const [refFailed, setRefFailed] = useState(false);
  const pointers = useRef(new Map<number, Pointer>());

  const frameRatio = refRatio ? framedRatio(refRatio, reference.framing) : null;
  const fit = shotRatio && frameRatio ? initialFit(shotRatio, frameRatio, reference.id) : null;

  // Once both photos' shapes are known: the saved framing (in the frame's
  // shape), or the largest centred crop.
  useEffect(() => {
    if (!shotRatio || !frameRatio || framing) return;
    setFraming(
      shot.framing
        ? withAspect(shot.framing, shotRatio, frameRatio)
        : initialFit(shotRatio, frameRatio, reference.id)
    );
  }, [shotRatio, frameRatio, framing, shot.framing, reference.id]);

  const latest = useRef({ framing, shotRatio });
  latest.current = { framing, shotRatio };

  /** Change the framing from the latest one (pointer handlers outlive renders). */
  const update = useCallback((change: (f: PhotoFraming, ratio: number) => PhotoFraming) => {
    const { framing: current, shotRatio: ratio } = latest.current;
    if (!current || !ratio) return;
    const next = change(current, ratio);
    latest.current = { framing: next, shotRatio: ratio };
    setFraming(next);
  }, []);

  const frameRect = (): DOMRect | null => {
    const rect = frameRef.current?.getBoundingClientRect();
    return rect && rect.width > 0 && rect.height > 0 ? rect : null;
  };

  // ctrl+wheel (and a trackpad pinch, which arrives as one) zooms about the
  // pointer; a plain wheel or two-finger scroll moves the shot. Not passive, so
  // the browser doesn't zoom the page as well.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;
    const onWheel = (event: WheelEvent): void => {
      const rect = frameRect();
      if (!rect) return;
      event.preventDefault();
      const moved = wheelPixels(event, rect);
      if (event.ctrlKey) {
        // A mouse notch is clamped, so it zooms by a step rather than a leap
        const delta = Math.max(-WHEEL_ZOOM_MAX, Math.min(WHEEL_ZOOM_MAX, moved.y));
        const factor = Math.exp(-delta * 0.01);
        const u = (event.clientX - rect.left) / rect.width;
        const v = (event.clientY - rect.top) / rect.height;
        update((f, ratio) => zoomAbout(f, factor, u, v, ratio));
      } else {
        update((f, ratio) => panBy(f, -moved.x / rect.width, -moved.y / rect.height, ratio));
      }
    };
    frame.addEventListener('wheel', onWheel, { passive: false });
    return () => frame.removeEventListener('wheel', onWheel);
  }, [update]);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (pointers.current.size >= 2) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    try {
      frameRef.current?.setPointerCapture(event.pointerId);
    } catch {
      // The pointer is already gone.
    }
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
    const previous = pointers.current.get(event.pointerId);
    const rect = frameRect();
    if (!previous || !rect) return;
    const now = { x: event.clientX, y: event.clientY };
    if (pointers.current.size === 1) {
      const dx = (now.x - previous.x) / rect.width;
      const dy = (now.y - previous.y) / rect.height;
      update((f, ratio) => panBy(f, dx, dy, ratio));
    } else {
      // Two fingers: zoom by the change in their spread, about their midpoint,
      // and move with the midpoint.
      const other = [...pointers.current.entries()].find(([id]) => id !== event.pointerId)?.[1];
      if (other) {
        const before = Math.hypot(previous.x - other.x, previous.y - other.y);
        const after = Math.hypot(now.x - other.x, now.y - other.y);
        const midBefore = { x: (previous.x + other.x) / 2, y: (previous.y + other.y) / 2 };
        const midAfter = { x: (now.x + other.x) / 2, y: (now.y + other.y) / 2 };
        const u = (midAfter.x - rect.left) / rect.width;
        const v = (midAfter.y - rect.top) / rect.height;
        update((f, ratio) => {
          const moved = panBy(
            f,
            (midAfter.x - midBefore.x) / rect.width,
            (midAfter.y - midBefore.y) / rect.height,
            ratio
          );
          return before > 0 && after > 0 ? zoomAbout(moved, after / before, u, v, ratio) : moved;
        });
      }
    }
    pointers.current.set(event.pointerId, now);
  };

  const endPointer = (event: PointerEvent<HTMLDivElement>): void => {
    pointers.current.delete(event.pointerId);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const pan = event.shiftKey ? KEY_PAN_FAST : KEY_PAN;
    const turn = event.shiftKey ? KEY_TURN_FAST : KEY_TURN;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-pan, 0],
      ArrowRight: [pan, 0],
      ArrowUp: [0, -pan],
      ArrowDown: [0, pan],
    };
    const move = moves[event.key];
    if (move) {
      update((f, ratio) => panBy(f, move[0], move[1], ratio));
    } else if (event.key === '+' || event.key === '=') {
      update((f, ratio) => zoomAbout(f, KEY_ZOOM, 0.5, 0.5, ratio));
    } else if (event.key === '-' || event.key === '_') {
      update((f, ratio) => zoomAbout(f, 1 / KEY_ZOOM, 0.5, 0.5, ratio));
    } else if (event.key === '[' || event.key === '{') {
      update((f) => rotateTo(f, f.rotation - turn));
    } else if (event.key === ']' || event.key === '}') {
      update((f) => rotateTo(f, f.rotation + turn));
    } else {
      return;
    }
    event.preventDefault();
  };

  const onSave = async (): Promise<void> => {
    if (!framing) return;
    setSaving(true);
    try {
      await save(shot.id, forSaving(framing, reference.id));
      onClose();
    } catch {
      // The caller says what went wrong; the editor stays open to try again.
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  const zoom = framing && fit ? zoomOf(framing, fit) : 1;
  const ready = !!framing && !!fit;
  const frameStyle = {
    '--frame-ratio': String(frameRatio ?? 4 / 3),
    '--ghost-opacity': String(opacity / 100),
  } as CSSProperties;
  const shotSrc = fullLoaded || !shot.photo_display_url ? shot.photo_url : shot.photo_display_url;

  return (
    <>
      <Group className={classes.top} justify="space-between" wrap="nowrap" gap="xs">
        <Box miw={0}>
          <Title order={2} className={classes.title}>
            Line up
          </Title>
          <Text size="sm" c="gray.4" truncate className={classes.tabular}>
            {formatPhotoDay(shot.entry_date)} against the reference {words.noun}
          </Text>
        </Box>
        <ActionIcon
          variant="subtle"
          color="gray.0"
          size={44}
          onClick={onClose}
          aria-label="Close line up"
        >
          <IconX size={24} />
        </ActionIcon>
      </Group>

      <Box className={classes.stage}>
        <Box
          ref={frameRef}
          className={classes.frame}
          style={frameStyle}
          tabIndex={0}
          role="group"
          aria-label={`${capitalise(words.noun)} to line up. Drag or use the arrow keys to move it, + and - to zoom, [ and ] to turn`}
          aria-busy={!ready}
          data-testid="line-up-frame"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endPointer}
          onPointerCancel={endPointer}
          onLostPointerCapture={endPointer}
          onKeyDown={onKeyDown}
          onDragStart={(event) => event.preventDefault()}
        >
          {shotSrc && (
            <img
              className={classes.shot}
              src={shotSrc}
              alt={`${words.nounTitle}, ${formatPhotoDay(shot.entry_date)}`}
              draggable={false}
              data-ready={ready || undefined}
              style={framing ? framedImageStyle(framing) : undefined}
              onLoad={(event) => {
                const ratio = ratioOf(event);
                if (ratio) setShotRatio(ratio);
              }}
            />
          )}
          {/* The full variant loads alongside, out of sight, then takes over */}
          {!fullLoaded && shot.photo_url && shot.photo_display_url && (
            <img
              className={classes.preload}
              src={shot.photo_url}
              alt=""
              aria-hidden
              onLoad={() => setFullLoaded(true)}
            />
          )}
          {reference.displayUrl && !refFailed && (
            <Box
              className={classes.ghost}
              data-style={ghostStyle}
              data-hidden={ghostHidden || undefined}
              data-testid="line-up-ghost"
            >
              <img
                src={reference.displayUrl}
                alt=""
                aria-hidden
                draggable={false}
                className={reference.framing ? undefined : classes.ghostWhole}
                style={reference.framing ? framedImageStyle(reference.framing) : undefined}
                onLoad={(event) => {
                  const ratio = ratioOf(event);
                  if (ratio) setRefRatio(ratio);
                }}
                onError={() => setRefFailed(true)}
              />
            </Box>
          )}
        </Box>
      </Box>

      <Stack className={classes.controls} gap="sm">
        {(refFailed || !reference.displayUrl) && (
          <Text size="sm" c="coral.3" role="alert">
            The reference {words.noun} didn&apos;t load. Close and try again.
          </Text>
        )}
        <Group gap="sm" wrap="wrap" align="center">
          <SegmentedControl
            value={ghostStyle}
            onChange={(value) => setGhostStyle(value as GhostStyle)}
            data={GHOST_STYLES}
            size="sm"
            aria-label="How the reference shows"
          />
          {ghostStyle === 'ghost' && (
            <Slider
              className={classes.opacity}
              value={opacity}
              onChange={setOpacity}
              min={10}
              max={90}
              step={5}
              label={(value) => `${value}%`}
              thumbLabel="Reference opacity"
              color="teal"
              classNames={SLIDER_CLASSES}
            />
          )}
          <ActionIcon
            variant="subtle"
            color="gray.0"
            size={44}
            aria-label="Hold to hide the reference"
            aria-pressed={ghostHidden}
            onPointerDown={() => setGhostHidden(true)}
            onPointerUp={() => setGhostHidden(false)}
            onPointerLeave={() => setGhostHidden(false)}
            onPointerCancel={() => setGhostHidden(false)}
            onKeyDown={(event) => {
              if (event.key === ' ' || event.key === 'Enter') setGhostHidden(true);
            }}
            onKeyUp={() => setGhostHidden(false)}
            onBlur={() => setGhostHidden(false)}
          >
            <IconEyeOff size={16} />
          </ActionIcon>
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" verticalSpacing={4}>
          <Box>
            <Text size="xs" c="gray.4" aria-hidden>
              Zoom
            </Text>
            <Slider
              value={zoom}
              min={ZOOM_MIN}
              max={ZOOM_MAX}
              step={0.01}
              disabled={!ready}
              label={(value) => `${value.toFixed(2)}x`}
              onChange={(value) =>
                update((f, ratio) => zoomAbout(f, value / zoomOf(f, fit ?? f), 0.5, 0.5, ratio))
              }
              thumbLabel="Zoom"
              color="teal"
              classNames={SLIDER_CLASSES}
            />
          </Box>
          <Box>
            <Text size="xs" c="gray.4" aria-hidden>
              Turn
            </Text>
            <Slider
              value={framing?.rotation ?? 0}
              min={-MAX_ROTATION}
              max={MAX_ROTATION}
              step={0.1}
              disabled={!ready}
              label={(value) => `${value.toFixed(1)}°`}
              marks={[{ value: 0 }]}
              onChange={(value) => update((f) => rotateTo(f, value))}
              thumbLabel="Turn"
              color="teal"
              classNames={SLIDER_CLASSES}
            />
          </Box>
        </SimpleGrid>

        <Group justify="space-between" gap="xs" wrap="nowrap">
          <Button
            variant="subtle"
            color="gray.0"
            leftSection={<IconArrowBackUp size={16} />}
            onClick={() => fit && setFraming(fit)}
            disabled={!ready}
          >
            Reset to fit
          </Button>
          <Group gap="xs" wrap="nowrap">
            <Button variant="subtle" color="gray.0" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={() => void onSave()} loading={saving} disabled={!ready}>
              Save
            </Button>
          </Group>
        </Group>
      </Stack>
    </>
  );
}
